import { Component, OnDestroy } from '@angular/core';
import { AgentService, ApiService, ShareService } from '@netfoundry-ui/shared/services';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Observable, Subject, takeUntil } from 'rxjs';

export type SparklineFetcher = (item: any) => Observable<{ rx: number; tx: number }[]>;

@Component({
    selector: 'app-table-cell-sparklines',
    templateUrl: './table-cell-sparklines.component.html',
    styleUrls: ['./table-cell-sparklines.component.scss'],
    standalone: false,
})
export class TableCellSparklinesComponent implements ICellRendererAngularComp, OnDestroy {
    params: ICellRendererParams;
    isV2 = false;
    accountLoaded = false;
    fetchSamples: SparklineFetcher | null = null;
    item: any = { name: undefined, frontdoorId: undefined, id: undefined };
    private destroy$ = new Subject<void>();
    data = {
        labels: [] as string[],
        datasets: [
            {
                data: [] as number[],
                borderColor: 'blue',
                backgroundColor: 'lightblue',
                fill: false,
                tension: 0.3,
                pointRadius: 0,
            },
            {
                data: [] as number[],
                borderColor: 'green',
                backgroundColor: 'lightgreen',
                fill: false,
                tension: 0.3,
                pointRadius: 0,
            },
        ],
    };
    options: any = {
        responsive: false,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
        },
        scales: {
            x: { display: false },
            y: { display: false, min: 0, max: 1 },
        },
        elements: {
            line: { borderWidth: 2 },
        },
    };

    constructor(
        private shareService: ShareService,
        public apiService: ApiService,
        public agentService: AgentService
    ) {}

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.item = params.node.data ?? {};
        this.fetchSamples = (params as any).fetchSamples || null;
        this.apiService.currentTenant.pipe(takeUntil(this.destroy$)).subscribe((currentTenant) => {
            this.agentService.getAccountById(currentTenant.id).pipe(takeUntil(this.destroy$)).subscribe((frontdoor: any) => {
                this.isV2 = frontdoor?.version !== 1;
                this.accountLoaded = true;
                this.loadSparklines();
            });
        });
    }

    refresh(params: ICellRendererParams): boolean {
        this.item = params.data ?? {};
        if (this.accountLoaded) {
            this.loadSparklines();
        }
        return true;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadSparklines() {
        if (this.fetchSamples) {
            this.fetchSamples(this.item).pipe(takeUntil(this.destroy$)).subscribe({
                next: (samples) => {
                    this.applySamples(samples.length > 0 ? samples : this.emptyFallback());
                },
                error: () => {
                    this.applySamples(this.emptyFallback());
                },
            });
        } else {
            this.loadDefaultSparklines();
        }
    }

    private loadDefaultSparklines() {
        const identifier = this.isV2 ? this.item?.id : this.item?.name;
        const frontdoorId = this.item?.frontdoorId;
        if (!frontdoorId || !identifier) {
            this.applySamples([]);
            return;
        }
        this.shareService.getSparklinesByShare(frontdoorId, identifier, this.isV2).pipe(takeUntil(this.destroy$)).subscribe({
            next: (data: any) => {
                const points = data?.sparklines?.[0]?.samples ?? this.emptyFallback();
                this.applySamples(points);
            },
            error: () => {
                this.applySamples(this.emptyFallback());
            },
        });
    }

    private applySamples(samples: any[]) {
        const rxData: number[] = [];
        const txData: number[] = [];
        const labels: string[] = [];
        for (const sample of samples) {
            rxData.push(+sample.rx || 0);
            txData.push(+sample.tx || 0);
            labels.push('');
        }
        this.data.datasets[0].data = rxData;
        this.data.datasets[1].data = txData;
        this.data.labels = labels;
        const allData = [...rxData, ...txData];
        const chartMax = Math.max(...allData, 0) || 1;
        this.options = {
            ...this.options,
            scales: {
                x: { display: false },
                y: { display: false, min: 0, max: chartMax + chartMax * 0.1 },
            },
        };
    }

    private emptyFallback() {
        return Array.from({ length: 7 }, () => ({ rx: 0, tx: 0 }));
    }
}
